import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

export const ParticlesBackground = ({ 
  particleCount = 50, 
  colors = ['gold', 'blue'],
  className = '' 
}) => {
  const containerRef = useRef(null);

  const particles = Array.from({ length: particleCount }, (_, i) => ({
    id: i,
    size: Math.random() * 6 + 2,
    x: Math.random() * 100,
    delay: Math.random() * 20,
    duration: Math.random() * 15 + 15,
    color: colors[Math.floor(Math.random() * colors.length)],
  }));

  return (
    <div ref={containerRef} className={`particles-container ${className}`}>
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className={`particle ${particle.color === 'gold' ? 'particle-gold' : 'particle-blue'}`}
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            bottom: '-10%',
          }}
          animate={{
            y: [0, -window.innerHeight * 1.2],
            opacity: [0, 1, 1, 0],
            scale: [0, 1, 1, 0.5],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
};

export const FloatingOrbs = ({ className = '' }) => {
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Large gold orb - enhanced glow */}
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(45 100% 60% / 0.12) 0%, hsl(45 100% 50% / 0.05) 40%, transparent 70%)',
          top: '-10%',
          right: '-10%',
          filter: 'blur(2px)',
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.6, 0.9, 0.6],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      
      {/* Cosmic purple orb */}
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(270 50% 50% / 0.08) 0%, transparent 70%)',
          bottom: '20%',
          left: '-15%',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 3,
        }}
      />
      
      {/* Blue orb */}
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(185 80% 45% / 0.12) 0%, transparent 70%)',
          bottom: '10%',
          left: '-5%',
        }}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
      />
      
      {/* Small floating particles - enhanced with glow */}
      {Array.from({ length: 16 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: i % 3 === 0 ? '4px' : '3px',
            height: i % 3 === 0 ? '4px' : '3px',
            background: i % 3 === 0 
              ? 'hsl(45 100% 70%)' 
              : i % 3 === 1 
                ? 'hsl(185 80% 55%)' 
                : 'hsl(270 60% 65%)',
            left: `${8 + (i * 5.5)}%`,
            top: `${15 + (i * 4.5) % 70}%`,
            boxShadow: i % 3 === 0 
              ? '0 0 10px hsl(45 100% 60% / 0.6)' 
              : i % 3 === 1 
                ? '0 0 8px hsl(185 80% 50% / 0.5)' 
                : '0 0 8px hsl(270 60% 60% / 0.4)',
          }}
          animate={{
            y: [0, -40, 0],
            x: [0, i % 2 === 0 ? 10 : -10, 0],
            opacity: [0.4, 0.9, 0.4],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 4 + (i * 0.4),
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.25,
          }}
        />
      ))}
      
      {/* Twinkling stars effect */}
      {Array.from({ length: 20 }).map((_, i) => (
        <motion.div
          key={`star-${i}`}
          className="absolute rounded-full"
          style={{
            width: '2px',
            height: '2px',
            background: 'hsl(210 40% 96%)',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            boxShadow: '0 0 4px hsl(210 40% 96% / 0.8)',
          }}
          animate={{
            opacity: [0.2, 1, 0.2],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: Math.random() * 3,
          }}
        />
      ))}
    </div>
  );
};

export default ParticlesBackground;
